import { Group, Stack, Text, Progress, Box } from '@mantine/core'
import { AreaChart, Area, ResponsiveContainer } from 'recharts'
import { WidgetCard } from '../../components/ui/WidgetCard'
import { CardHeader } from '../../components/ui/CardHeader'
import { useJiraSprints, useJiraVelocity } from '../../hooks/useJiraData'

export function JiraSprintWidget() {
  const { data: sprints, isLoading: sprintsLoading, isError: sprintsError } = useJiraSprints('active')
  const { data: velocity, isLoading: velocityLoading, isError: velocityError } = useJiraVelocity(6)

  if (sprintsError || sprintsLoading || !sprints?.length) return null

  const sprint = sprints[0]
  const total = sprint.total_story_points || 0
  const completed = sprint.completed_story_points || 0
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0

  // Days remaining
  const endDate = sprint.end_date ? new Date(sprint.end_date) : null
  const now = new Date()
  const daysLeft = endDate ? Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / 86400000)) : null

  const badgeColor = daysLeft === null ? 'gray' : daysLeft <= 1 ? 'red' : daysLeft <= 3 ? 'orange' : 'green'
  const badgeLabel = daysLeft !== null ? `${daysLeft}d restantes` : 'Sprint ativo'

  // Velocity sparkline data
  const sparkData = (!velocityError && !velocityLoading && velocity?.sprints)
    ? velocity.sprints.map((s: { name: string; completed_story_points: number }) => ({
        name: s.name,
        sp: s.completed_story_points,
      }))
    : []

  const avgVelocity = velocity?.average_velocity ?? null
  const trend = velocity?.trend ?? null
  const trendArrow = trend === 'improving' ? '↗' : trend === 'declining' ? '↘' : '→'

  return (
    <WidgetCard accent="success" p={0}>
      <CardHeader
        title="Sprint Ativo"
        badge={{ value: badgeLabel, color: badgeColor }}
      />
      <Box p="md">
        <Group align="flex-start" grow gap="lg">
          {/* Left: Sprint Progress */}
          <Stack gap="xs">
            <Text fw={600} size="sm">{sprint.name}</Text>
            <Progress value={progress} size="md" color="teal" />
            <Text size="xs" c="dimmed">{completed} / {total} SP</Text>
          </Stack>

          {/* Right: Velocity Sparkline */}
          <Stack gap={4}>
            <Text size="xs" fw={500} c="dimmed">Velocity (últimos 6)</Text>
            {sparkData.length > 0 ? (
              <ResponsiveContainer width="100%" height={60}>
                <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                  <defs>
                    <linearGradient id="spGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#098d93" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#098d93" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="sp"
                    stroke="#098d93"
                    fill="url(#spGrad)"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box h={60} />
            )}
            {avgVelocity !== null && (
              <Text size="xs" c="dimmed">
                Média: {avgVelocity} SP {trendArrow}
              </Text>
            )}
          </Stack>
        </Group>
      </Box>
    </WidgetCard>
  )
}
